import React, { useState, useEffect, useMemo, useRef } from 'react';
import './styles.css';
import Navigation from './components/Navigation.js';
import HeroSection from './components/HeroSection.js';
import AboutSection from './components/AboutSection.js';
import ProjectsSection from './components/ProjectsSection.js';
import ContactSection from './components/ContactSection.js';
// projects.json is served from public/, fetch at runtime instead of static import
import AdminApp from './admin.js';
import { useLocation} from 'react-router-dom';


function App() {
    const [activeSection, setActiveSection] = useState('home');
    const [projectsData, setProjectsData] = useState([]);
    const [heroOpacity, setHeroOpacity] = useState(1);
    const [sortOrder, setSortOrder] = useState('featured');
    const [selectedTags, setSelectedTags] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [lockedHeight, setLockedHeight] = useState(null);

    // Lock viewport height on mobile to prevent address bar resizing
    useEffect(() => {
      if (window.innerWidth <= 768) {
        const initialVH = window.innerHeight;
        setLockedHeight(initialVH);
        document.documentElement.style.setProperty('--initial-vh', `${initialVH}px`);
        
        // Update CSS custom property that components can use
        const root = document.getElementById('root');
        if (root) {
          root.style.minHeight = `${initialVH}px`;
        }
      }
    }, []);

    // Handle hero fade on scroll
    useEffect(() => {
      const handleScroll = () => {
        const maxFade = 600;
        const opacity = Math.max(1 - window.scrollY / maxFade, 0);
        setHeroOpacity(opacity);
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // load project data from public/projects.json
    useEffect(() => {
      const url = `${process.env.PUBLIC_URL}/projects.json`;
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
          return res.json();
        })
        .then(data => setProjectsData(data.projects || []))
        .catch(err => console.error('Error loading projects:', err));
    }, []);

    const skills = useMemo(() => {
    const setSkills = new Set();
    projectsData.forEach(p => p.tags.forEach(tag => setSkills.add(tag)));
    return Array.from(setSkills);
    }, [projectsData]);

    const filteredProjects = useMemo(() => {
        let result = [...projectsData];
        
        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter(project => 
                project.title.toLowerCase().includes(query) ||
                project.description.toLowerCase().includes(query) ||
                project.tags.some(tag => tag.toLowerCase().includes(query)) ||
                project.category.toLowerCase().includes(query)
            );
        }
        
        // Apply tags filter
        if (selectedTags.length > 0) {
            result = result.filter(project => 
                selectedTags.some(tag => project.tags.includes(tag))
            );
        }
        
        // Helper function to convert project date to comparable timestamp
        const getProjectDate = (project) => {
            // Use endDate if available, treating "Present" as current date
            if (project.endDate) {
                if (project.endDate === 'Present') {
                    return new Date().getTime(); // Current date for ongoing projects
                }
                return new Date(project.endDate + 'T00:00:00').getTime();
            }
            // Fallback to startDate if endDate not available
            if (project.startDate) {
                return new Date(project.startDate + 'T00:00:00').getTime();
            }
            // Fallback to month/year system
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                              'July', 'August', 'September', 'October', 'November', 'December'];
            const monthIndex = monthNames.indexOf(project.month || 'January');
            const year = parseInt(project.year) || new Date().getFullYear();
            return new Date(year, monthIndex).getTime();
        };
        
        // Apply sort
        if (sortOrder === 'featured') {
            result.sort((a, b) => {
                // Featured projects first, then by newest
                if (a.featured && !b.featured) return -1;
                if (!a.featured && b.featured) return 1;
                return getProjectDate(b) - getProjectDate(a);
            });
        } else {
            result.sort((a, b) => sortOrder === 'newest' ? 
                getProjectDate(b) - getProjectDate(a) : 
                getProjectDate(a) - getProjectDate(b)
            );
        }
        
        return result;
    }, [projectsData, searchQuery, selectedTags, sortOrder]);

  // custom smooth scroll with adjustable duration and interrupt support
    const scrollAnimRef = useRef(null);
    const smoothScrollTo = (targetY, duration = 1500) => {
      // cancel any ongoing scroll animation
      if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
      const startY = window.pageYOffset;
      const distance = targetY - startY;
      const startTime = performance.now();
      // use a power-easing function so the scroll starts slower, accelerates,
      // then decelerates at the end. Power > 2 gives a more pronounced slow start.
      const easeInOutPow = (t, power = 3) => {
        if (t < 0.5) {
          return 0.5 * Math.pow(2 * t, power);
        }
        return 1 - 0.5 * Math.pow(2 * (1 - t), power);
      };
      const step = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
  const ease = easeInOutPow(progress, 3);
        window.scrollTo(0, startY + distance * ease);
        if (elapsed < duration) {
          scrollAnimRef.current = requestAnimationFrame(step);
        } else {
          scrollAnimRef.current = null;
        }
      };
      scrollAnimRef.current = requestAnimationFrame(step);
    };
    // cancel custom scroll on user interaction
    useEffect(() => {
      const cancelScroll = () => {
        if (scrollAnimRef.current) {
          cancelAnimationFrame(scrollAnimRef.current);
          scrollAnimRef.current = null;
        }
      };
      window.addEventListener('wheel', cancelScroll, { passive: true });
      window.addEventListener('touchstart', cancelScroll, { passive: true });
      window.addEventListener('mousedown', cancelScroll);
      return () => {
        window.removeEventListener('wheel', cancelScroll);
        window.removeEventListener('touchstart', cancelScroll);
        window.removeEventListener('mousedown', cancelScroll);
      };
    }, []);
    const scrollToSection = (id) => {
      // always use custom scroll when hero is visible (drone animation phase)
      const useCustom = heroOpacity > 0;
      if (id === 'home') {
        if (useCustom) smoothScrollTo(0);
        else window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const el = document.getElementById(id);
      if (!el) return;
      let offsetY = el.getBoundingClientRect().top + window.pageYOffset - 80;
      // prevent overshoot beyond document bottom
      const maxY = document.documentElement.scrollHeight - window.innerHeight;
      offsetY = Math.min(offsetY, maxY);
      if (useCustom) smoothScrollTo(offsetY);
      else window.scrollTo({ top: offsetY, behavior: 'smooth' });
    };

    // allow admin page in dev
    const loc = useLocation();
    if (loc.pathname === '/admin') {
        if (process.env.NODE_ENV === 'development') {
             return <AdminApp />;
        } else {
            window.location.href = "/";
            return null;
        }
    }

  return (
    <div className="overflow-x-hidden">
        
      <div className="font-sans bg-black text-white mobile-min-vh-100">
          {/* Navigation is fixed */}
          <Navigation activeSection={activeSection} scrollToSection={scrollToSection} />
          
          {/* HeroSection is fixed and fades out */}
          <HeroSection opacity={heroOpacity} scrollToSection={scrollToSection} />

          {/* This creates the space for the hero to be seen before the content starts */}
          <div className="relative z-20" style={{ pointerEvents: heroOpacity < 0.4 ? 'auto' : 'none' }}>
          {/* Fixed spacer using locked height instead of responsive viewport */}
          <div className="pointer-events-none" style={{ 
            height: lockedHeight ? `${lockedHeight}px` : '100vh'
          }}></div>
          <div style={{ height: '300px' }} className="pointer-events-none"></div>
          
          {/* Your scrolling content sections */}
          <AboutSection scrollToSection={scrollToSection} />
          <ProjectsSection
              skills={skills}
              filteredProjects={filteredProjects}
              sortOrder={sortOrder}
              selectedTags={selectedTags}
              searchQuery={searchQuery}
              showFilterModal={showFilterModal}
              setSortOrder={setSortOrder}
              setSelectedTags={setSelectedTags}
              setSearchQuery={setSearchQuery}
              setShowFilterModal={setShowFilterModal}
          />
          <ContactSection />
        </div>
      </div>
    </div>
  );
}

export default App;